import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import mammoth from 'mammoth';
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
import { saveAs } from 'file-saver';
import {
  ArrowLeftIcon,
  PlusIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const AITemplates = ({ showNotification }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    type: 'email',
    category: 'general',
    description: '',
    content: '',
    variables: [],
    tags: [],
    fileFormat: 'text', // 'text' or 'docx'
    docxFile: null // Store DOCX file for upload
  });
  const [newVariable, setNewVariable] = useState('');
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/ai/templates');
      setTemplates(response.data.data || []);
    } catch (error) {
      logger.error('Error fetching templates:', error);
      showNotification?.('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async () => {
    try {
      setCreating(true);
      await axiosInstance.post('/api/ai/templates', newTemplate);
      fetchTemplates();
      setShowCreateModal(false);
      resetForm();
      showNotification?.('Template created successfully', 'success');
    } catch (error) {
      logger.error('Error creating template:', error);
      showNotification?.(error.response?.data?.message || 'Failed to create template', 'error');
    } finally {
      setCreating(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await axiosInstance.delete(`/api/ai/templates/${templateId}`);
      fetchTemplates();
      showNotification?.('Template deleted successfully', 'success');
    } catch (error) {
      logger.error('Error deleting template:', error);
      showNotification?.('Failed to delete template', 'error');
    }
  };

  const updateTemplate = async () => {
    try {
      setUpdating(true);
      await axiosInstance.put(`/api/ai/templates/${selectedTemplate._id}`, newTemplate);
      fetchTemplates();
      setShowEditModal(false);
      setSelectedTemplate(null);
      resetForm();
      showNotification?.('Template updated successfully', 'success');
    } catch (error) {
      logger.error('Error updating template:', error);
      showNotification?.(error.response?.data?.message || 'Failed to update template', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const duplicateTemplate = async (template) => {
    try {
      await axiosInstance.post(`/api/ai/templates/${template._id}/duplicate`);
      fetchTemplates();
      showNotification?.('Template duplicated successfully', 'success');
    } catch (error) {
      logger.error('Error duplicating template:', error);
      showNotification?.('Failed to duplicate template', 'error');
    }
  };

  const openEditModal = (template) => {
    setSelectedTemplate(template);
    setNewTemplate({
      name: template.name,
      type: template.type,
      category: template.category || 'general',
      description: template.description || '',
      content: template.content,
      variables: template.variables || [],
      tags: template.tags || []
    });
    setShowEditModal(true);
  };

  const detectTemplateType = (fileName, content) => {
    const nameLower = fileName.toLowerCase();
    const contentLower = content.toLowerCase();

    // Check filename first
    if (nameLower.includes('contract') || contentLower.includes('this agreement') || contentLower.includes('parties agree')) {
      return 'contract';
    }
    if (nameLower.includes('offer') && nameLower.includes('letter') || contentLower.includes('offer of employment')) {
      return 'offer_letter';
    }
    if (nameLower.includes('joining') || nameLower.includes('welcome') && contentLower.includes('first day')) {
      return 'joining_letter';
    }
    if (nameLower.includes('termination') || contentLower.includes('employment termination')) {
      return 'termination_letter';
    }
    if (nameLower.includes('invoice') || contentLower.includes('invoice number') || contentLower.includes('amount due')) {
      return 'invoice';
    }
    if (nameLower.includes('quotation') || nameLower.includes('quote') || contentLower.includes('price quote')) {
      return 'quotation';
    }
    if (nameLower.includes('nda') || nameLower.includes('non-disclosure') || contentLower.includes('confidential information')) {
      return 'nda';
    }
    if (nameLower.includes('policy') || contentLower.includes('policy document')) {
      return 'policy';
    }
    if (nameLower.includes('email') || nameLower.includes('mail')) {
      return 'email';
    }

    // Default to email for unknown types
    return 'email';
  };

  // Extract text and variables from DOCX file
  const extractTextFromDOCX = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      logger.error('Error extracting DOCX text:', error);
      throw new Error('Failed to extract text from DOCX');
    }
  };

  // Extract variables from template content ({{variable_name}} format)
  const extractVariables = (content) => {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variables = [];
    let match;

    while ((match = variableRegex.exec(content)) !== null) {
      const varName = match[1].trim();
      if (!variables.includes(varName)) {
        variables.push(varName);
      }
    }

    return variables;
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const fileName = file.name.replace(/\.[^/.]+$/, '');

    // DOCX templates only - reject other formats
    if (fileExtension !== '.docx') {
      showNotification?.(
        'Templates must be in DOCX format. Please upload a .docx file with variables like {{variable_name}}.',
        'error'
      );
      event.target.value = '';
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showNotification?.('File size must be less than 10MB', 'error');
      event.target.value = '';
      return;
    }

    try {
      setUploading(true);
      showNotification?.('Processing DOCX template...', 'info');

      // Extract text content for preview
      const extractedContent = await extractTextFromDOCX(file);

      // Validate extracted content
      if (!extractedContent || extractedContent.trim().length === 0) {
        showNotification?.('No text content found in DOCX file', 'error');
        setUploading(false);
        event.target.value = '';
        return;
      }

      // Extract variables from content ({{variable_name}} format)
      const detectedVariables = extractVariables(extractedContent);

      // Detect template type automatically
      const detectedType = detectTemplateType(fileName, extractedContent);

      // Convert file to Base64 for storage
      const arrayBuffer = await file.arrayBuffer();
      const base64String = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      // Pre-fill the create modal with file data
      setNewTemplate({
        name: fileName,
        type: detectedType,
        category: 'general',
        description: `Uploaded from ${file.name}`,
        content: extractedContent, // Text preview
        variables: detectedVariables,
        tags: [],
        fileFormat: 'docx',
        docxFile: base64String // Store base64 encoded DOCX
      });

      setShowCreateModal(true);
      showNotification?.(
        `DOCX template loaded successfully! Found ${detectedVariables.length} variable(s). Review and save.`,
        'success'
      );
      setUploading(false);
    } catch (error) {
      logger.error('Error uploading DOCX file:', error);
      showNotification?.(error.message || 'Failed to upload DOCX template', 'error');
      setUploading(false);
    }

    // Reset file input
    event.target.value = '';
  };

  const resetForm = () => {
    setNewTemplate({
      name: '',
      type: 'email',
      category: 'general',
      description: '',
      content: '',
      variables: [],
      tags: [],
      fileFormat: 'text',
      docxFile: null
    });
    setNewVariable('');
    setNewTag('');
  };

  const addVariable = () => {
    if (newVariable.trim() && !newTemplate.variables.includes(newVariable.trim())) {
      setNewTemplate({
        ...newTemplate,
        variables: [...newTemplate.variables, newVariable.trim()]
      });
      setNewVariable('');
    }
  };

  const removeVariable = (variable) => {
    setNewTemplate({
      ...newTemplate,
      variables: newTemplate.variables.filter(v => v !== variable)
    });
  };

  const addTag = () => {
    if (newTag.trim() && !newTemplate.tags.includes(newTag.trim())) {
      setNewTemplate({
        ...newTemplate,
        tags: [...newTemplate.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setNewTemplate({
      ...newTemplate,
      tags: newTemplate.tags.filter(t => t !== tag)
    });
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || template.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const getTypeIcon = (type) => {
    return <DocumentTextIcon className="w-5 h-5" />;
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      email: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      contract: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      offer_letter: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      joining_letter: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
      termination_letter: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      invoice: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
      quotation: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      nda: 'bg-gray-100 text-gray-700 dark:bg-[#222222] dark:text-gray-300',
      policy: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
      other: 'bg-gray-100 text-gray-700 dark:bg-[#222222] dark:text-gray-300'
    };
    return colors[type] || colors.other;
  };

  if (loading) {
    return (
      <div className="h-full bg-white dark:bg-[#111111] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white dark:bg-[#111111] overflow-y-auto">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/ai-dashboard')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Templates
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {templates.length} {templates.length === 1 ? 'template' : 'templates'} available
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowUpTrayIcon className="w-4 h-4" />
                {uploading ? 'Uploading...' : 'Upload Template'}
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Create Template
              </button>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search templates..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm"
              />
            </div>

            {/* Filter */}
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm"
              >
                <option value="all">All Types</option>
                <option value="email">Email Templates</option>
                <option value="contract">Contracts</option>
                <option value="offer_letter">Offer Letters</option>
                <option value="joining_letter">Joining Letters</option>
                <option value="termination_letter">Termination Letters</option>
                <option value="invoice">Invoices</option>
                <option value="quotation">Quotations</option>
                <option value="nda">NDAs</option>
                <option value="policy">Policies</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              {searchTerm || filterType !== 'all' ? 'No templates found' : 'No templates created yet'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {searchTerm || filterType !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Create templates manually or upload text files (.txt, .html)'}
            </p>
            {!searchTerm && filterType === 'all' && (
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors"
                >
                  Upload Template
                </button>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                >
                  Create First Template
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map((template) => (
              <div
                key={template._id}
                className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 hover:border-gray-300 dark:hover:border-gray-700 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
                    {getTypeIcon(template.type)}
                  </div>
                  <div className="relative group">
                    <button className="p-2 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors">
                      <EllipsisVerticalIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                      <button
                        onClick={() => {
                          setSelectedTemplate(template);
                          setShowViewModal(true);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#222222] flex items-center gap-2 rounded-t-lg"
                      >
                        <EyeIcon className="w-4 h-4" />
                        View
                      </button>
                      <button
                        onClick={() => openEditModal(template)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#222222] flex items-center gap-2"
                      >
                        <PencilIcon className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => duplicateTemplate(template)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#222222] flex items-center gap-2"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                        Duplicate
                      </button>
                      <button
                        onClick={() => deleteTemplate(template._id)}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2 rounded-b-lg"
                      >
                        <TrashIcon className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>

                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  {template.name}
                </h3>

                <div className="mb-3">
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${getTypeBadgeColor(template.type)}`}>
                    {template.type.replace('_', ' ').toUpperCase()}
                  </span>
                </div>

                {template.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                    {template.description}
                  </p>
                )}

                {template.variables && template.variables.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Variables:</p>
                    <div className="flex flex-wrap gap-1">
                      {template.variables.slice(0, 3).map((variable, idx) => (
                        <span key={idx} className="px-2 py-1 text-xs bg-gray-100 dark:bg-[#222222] text-gray-700 dark:text-gray-300 rounded">
                          {variable}
                        </span>
                      ))}
                      {template.variables.length > 3 && (
                        <span className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
                          +{template.variables.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {template.tags && template.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {template.tags.slice(0, 2).map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                        #{tag}
                      </span>
                    ))}
                    {template.tags.length > 2 && (
                      <span className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
                        +{template.tags.length - 2}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Template Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={() => !creating && setShowCreateModal(false)}
        >
          <div
            className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto transform transition-all animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-[#1a1a1a] z-10">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Create New Template
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Create a reusable template for AI agents to use
                  </p>
                </div>
                <button
                  onClick={() => !creating && setShowCreateModal(false)}
                  disabled={creating}
                  className="ml-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors disabled:opacity-50"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="e.g., Standard Offer Letter"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                />
              </div>

              {/* Type and Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Template Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newTemplate.type}
                    onChange={(e) => setNewTemplate({ ...newTemplate, type: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  >
                    <option value="email">Email Template</option>
                    <option value="contract">Contract</option>
                    <option value="offer_letter">Offer Letter</option>
                    <option value="joining_letter">Joining Letter</option>
                    <option value="termination_letter">Termination Letter</option>
                    <option value="invoice">Invoice</option>
                    <option value="quotation">Quotation</option>
                    <option value="nda">NDA</option>
                    <option value="policy">Policy Document</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    value={newTemplate.category}
                    onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  >
                    <option value="general">General</option>
                    <option value="hr">HR</option>
                    <option value="finance">Finance</option>
                    <option value="legal">Legal</option>
                    <option value="sales">Sales</option>
                    <option value="support">Support</option>
                    <option value="it">IT</option>
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                  placeholder="Describe when and how this template should be used..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm resize-none"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Template Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newTemplate.content}
                  onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
                  placeholder="Enter template content here. Use {{variable_name}} for dynamic variables..."
                  rows={10}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm font-mono resize-none"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use double curly braces for variables: {'{{candidate_name}}'}, {'{{company_name}}'}, {'{{date}}'}, etc.
                </p>
              </div>

              {/* Variables */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Variables (Optional)
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newVariable}
                    onChange={(e) => setNewVariable(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addVariable()}
                    placeholder="e.g., candidate_name"
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  />
                  <button
                    onClick={addVariable}
                    className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100"
                  >
                    Add
                  </button>
                </div>
                {newTemplate.variables.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {newTemplate.variables.map((variable, idx) => (
                      <span key={idx} className="px-3 py-1 bg-gray-100 dark:bg-[#222222] text-gray-700 dark:text-gray-300 text-sm rounded-full flex items-center gap-2">
                        {variable}
                        <button onClick={() => removeVariable(variable)} className="text-gray-500 hover:text-red-600">
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Tags (Optional)
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    placeholder="e.g., urgent, standard"
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  />
                  <button
                    onClick={addTag}
                    className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100"
                  >
                    Add
                  </button>
                </div>
                {newTemplate.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {newTemplate.tags.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded-full flex items-center gap-2">
                        #{tag}
                        <button onClick={() => removeTag(tag)} className="text-blue-500 hover:text-red-600">
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-[#111111]/50 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3 sticky bottom-0">
              <button
                onClick={() => {
                  if (!creating) {
                    setShowCreateModal(false);
                    resetForm();
                  }
                }}
                disabled={creating}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#1a1a1a] border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222] rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={createTemplate}
                disabled={creating || !newTemplate.name || !newTemplate.content}
                className="flex items-center gap-2 px-6 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating && (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {creating ? 'Creating...' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {showEditModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={() => !updating && setShowEditModal(false)}
        >
          <div
            className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto transform transition-all animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-[#1a1a1a] z-10">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Edit Template
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Update template details and content
                  </p>
                </div>
                <button
                  onClick={() => !updating && (setShowEditModal(false), resetForm(), setSelectedTemplate(null))}
                  disabled={updating}
                  className="ml-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors disabled:opacity-50"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body - Same as Create Modal */}
            <div className="px-6 py-5 space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="e.g., Standard Offer Letter"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                />
              </div>

              {/* Type and Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Template Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newTemplate.type}
                    onChange={(e) => setNewTemplate({ ...newTemplate, type: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  >
                    <option value="email">Email Template</option>
                    <option value="contract">Contract</option>
                    <option value="offer_letter">Offer Letter</option>
                    <option value="joining_letter">Joining Letter</option>
                    <option value="termination_letter">Termination Letter</option>
                    <option value="invoice">Invoice</option>
                    <option value="quotation">Quotation</option>
                    <option value="nda">NDA</option>
                    <option value="policy">Policy Document</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    value={newTemplate.category}
                    onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  >
                    <option value="general">General</option>
                    <option value="hr">HR</option>
                    <option value="finance">Finance</option>
                    <option value="legal">Legal</option>
                    <option value="sales">Sales</option>
                    <option value="support">Support</option>
                    <option value="it">IT</option>
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                  placeholder="Describe when and how this template should be used..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm resize-none"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Template Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newTemplate.content}
                  onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
                  placeholder="Enter template content here. Use {{variable_name}} for dynamic variables..."
                  rows={10}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm font-mono resize-none"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use double curly braces for variables: {'{{candidate_name}}'}, {'{{company_name}}'}, {'{{date}}'}, etc.
                </p>
              </div>

              {/* Variables */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Variables (Optional)
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newVariable}
                    onChange={(e) => setNewVariable(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addVariable()}
                    placeholder="e.g., candidate_name"
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  />
                  <button
                    onClick={addVariable}
                    className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100"
                  >
                    Add
                  </button>
                </div>
                {newTemplate.variables.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {newTemplate.variables.map((variable, idx) => (
                      <span key={idx} className="px-3 py-1 bg-gray-100 dark:bg-[#222222] text-gray-700 dark:text-gray-300 text-sm rounded-full flex items-center gap-2">
                        {variable}
                        <button onClick={() => removeVariable(variable)} className="text-gray-500 hover:text-red-600">
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Tags (Optional)
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    placeholder="e.g., urgent, standard"
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  />
                  <button
                    onClick={addTag}
                    className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100"
                  >
                    Add
                  </button>
                </div>
                {newTemplate.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {newTemplate.tags.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded-full flex items-center gap-2">
                        #{tag}
                        <button onClick={() => removeTag(tag)} className="text-blue-500 hover:text-red-600">
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-[#111111]/50 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3 sticky bottom-0">
              <button
                onClick={() => {
                  if (!updating) {
                    setShowEditModal(false);
                    resetForm();
                    setSelectedTemplate(null);
                  }
                }}
                disabled={updating}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#1a1a1a] border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222] rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={updateTemplate}
                disabled={updating || !newTemplate.name || !newTemplate.content}
                className="flex items-center gap-2 px-6 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating && (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {updating ? 'Updating...' : 'Update Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Template Modal */}
      {showViewModal && selectedTemplate && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowViewModal(false)}
        >
          <div
            className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {selectedTemplate.name}
                  </h2>
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded mt-2 ${getTypeBadgeColor(selectedTemplate.type)}`}>
                    {selectedTemplate.type.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              {selectedTemplate.description && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Description</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{selectedTemplate.description}</p>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Content</h3>
                <div className="p-4 bg-gray-50 dark:bg-[#111111] rounded-lg">
                  <pre className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap font-mono">
                    {selectedTemplate.content}
                  </pre>
                </div>
              </div>

              {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Variables</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.variables.map((variable, idx) => (
                      <span key={idx} className="px-3 py-1 bg-gray-100 dark:bg-[#222222] text-gray-700 dark:text-gray-300 text-sm rounded">
                        {variable}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedTemplate.tags && selectedTemplate.tags.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.tags.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AITemplates;
